// ===========================================
// Backup Codes Display Component
// ===========================================
// Shared sub-component for displaying backup codes with copy/download

import { useState } from 'react';
import { Alert, Box, Button, Grid, Stack } from '@mui/material';
import {
  ContentCopy as CopyIcon,
  Download as DownloadIcon,
  Check as CheckIcon,
} from '@mui/icons-material';

interface Props {
  codes: string[];
}

export default function BackupCodesDisplay({ codes }: Props) {
  const [copySuccess, setCopySuccess] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(codes.join('\n'));
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch {
      // Fallback for browsers without clipboard API
      const textarea = document.createElement('textarea');
      textarea.value = codes.join('\n');
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    }
  };

  const handleDownload = () => {
    const content = [
      'Music \'n Me - Two-Factor Authentication Backup Codes',
      '=====================================================',
      '',
      'Save these codes in a safe place. Each code can only be used once.',
      '',
      ...codes.map((code, i) => `${i + 1}. ${code}`),
      '',
      `Generated: ${new Date().toLocaleString()}`,
    ].join('\n');

    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'musicnme-2fa-backup-codes.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <Box>
      <Alert severity="warning" sx={{ mb: 2 }}>
        Save these codes in a safe place. Each code can only be used once.
        If you lose access to your authenticator app, you can use these codes to sign in.
      </Alert>

      <Grid container spacing={1} sx={{ mb: 2 }}>
        {codes.map((code) => (
          <Grid item xs={6} key={code}>
            <Box
              sx={{
                fontFamily: 'monospace',
                fontSize: '0.9rem',
                bgcolor: 'grey.100',
                px: 1.5,
                py: 1,
                borderRadius: 1,
                textAlign: 'center',
                letterSpacing: 1,
              }}
            >
              {code}
            </Box>
          </Grid>
        ))}
      </Grid>

      <Stack direction="row" spacing={1}>
        <Button
          variant="outlined"
          size="small"
          startIcon={copySuccess ? <CheckIcon /> : <CopyIcon />}
          onClick={handleCopy}
          color={copySuccess ? 'success' : 'primary'}
        >
          {copySuccess ? 'Copied!' : 'Copy All'}
        </Button>
        <Button
          variant="outlined"
          size="small"
          startIcon={<DownloadIcon />}
          onClick={handleDownload}
        >
          Download
        </Button>
      </Stack>
    </Box>
  );
}
